import { Form, FormTextInput, StandardFormButtons } from '@components/form';
import { ModalProps, registerCloseInterceptor, useConfirmation, useModal } from '@components/modal';
import {
  GetOrganizationRulesDocument,
  GetServiceRulesDocument,
  GetUserRulesDocument,
  GetWorkspaceRulesDocument,
  useEditOrganizationRulesMutation,
  useEditServiceRulesMutation,
  useEditUserRulesMutation,
  useEditWorkspaceRulesMutation,
} from '@generated/graphql';
import { Stack } from '@mui/material';
import { FormikBag } from 'formik';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import * as yup from 'yup';

const validationSchema = yup.object({
  rules: yup.string(),
});

type EditRulesModalProps = {
  currentRules?: string;
  reloadKey?: string;
  reloadRules?: string;
  workspaceId?: string;
  organizationId?: string;
  serviceId?: string;
  onClose: VoidFunction;
  modalKey: string;
};

const EditRulesModal = ({
  currentRules,
  reloadKey,
  reloadRules,
  workspaceId,
  organizationId,
  serviceId,
  onClose,
  modalKey,
}: EditRulesModalProps) => {
  const [editWorkspaceRules] = useEditWorkspaceRulesMutation();
  const [editOrganizationRules] = useEditOrganizationRulesMutation();
  const [editServiceRules] = useEditServiceRulesMutation();
  const [editUserRules] = useEditUserRulesMutation();
  const { showConfirmation } = useConfirmation();

  // Ref to track current dirty state for use in close interceptor
  const dirtyRef = useRef(false);

  useEffect(() => {
    registerCloseInterceptor(modalKey, (proceed) => {
      if (dirtyRef.current) {
        showConfirmation({
          onAffirm: () => {
            localStorage.removeItem(reloadKey || 'edit-rules');
            proceed();
          },
          message: 'You have unsaved changes. Are you sure you want to close?',
        });
      } else {
        localStorage.removeItem(reloadKey || 'edit-rules');
        proceed();
      }
    });
  }, [modalKey, reloadKey, showConfirmation]);

  const handleFormSubmit = async (
    { rules }: { rules: string },
    formik: FormikBag<unknown, unknown>,
  ) => {
    if (workspaceId) {
      posthog.capture('agent-studio:user_edit-workspace-rules_submitted', {
        workspaceId,
      });
      await editWorkspaceRules({
        variables: { workspaceId, rules },
        refetchQueries: [{ query: GetWorkspaceRulesDocument, variables: { workspaceId } }],
        onCompleted: () => {
          posthog.capture('agent-studio:user_edit-workspace-rules_completed', {
            workspaceId,
          });
          formik.resetForm({ values: { rules } });
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_edit-workspace-rules_failed', {
            workspaceId,
            error: (error as Error)?.message || 'An error occurred during edit',
          });
          throw error;
        },
      });
    } else if (organizationId) {
      posthog.capture('agent-studio:user_edit-organization-rules_submitted', {
        organizationId,
      });
      await editOrganizationRules({
        variables: { organizationId, rules },
        refetchQueries: [{ query: GetOrganizationRulesDocument, variables: { organizationId } }],
        onCompleted: () => {
          posthog.capture('agent-studio:user_edit-organization-rules_completed', {
            organizationId,
          });
          formik.resetForm({ values: { rules } });
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_edit-organization-rules_failed', {
            organizationId,
            error: (error as Error)?.message || 'An error occurred during edit',
          });
          throw error;
        },
      });
    } else if (serviceId) {
      posthog.capture('agent-studio:user_edit-service-rules_submitted', {
        serviceId,
      });
      await editServiceRules({
        variables: { serviceId, rules },
        refetchQueries: [{ query: GetServiceRulesDocument, variables: { serviceId } }],
        onCompleted: () => {
          posthog.capture('agent-studio:user_edit-service-rules_completed', {
            serviceId,
          });
          formik.resetForm({ values: { rules } });
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_edit-service-rules_failed', {
            serviceId,
            error: (error as Error)?.message || 'An error occurred during edit',
          });
          throw error;
        },
      });
    } else {
      posthog.capture('agent-studio:user_edit-user-rules_submitted');
      await editUserRules({
        variables: { rules },
        refetchQueries: [{ query: GetUserRulesDocument }],
        onCompleted: () => {
          posthog.capture('agent-studio:user_edit-user-rules_completed');
          formik.resetForm({ values: { rules } });
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_edit-user-rules_failed', {
            error: (error as Error)?.message || 'An error occurred during edit',
          });
          throw error;
        },
      });
    }
  };

  const handleSaveState = (values: { rules: string }) => {
    localStorage.setItem(reloadKey || 'edit-rules', values.rules);
  };

  return (
    <Form
      initialValues={{
        rules: currentRules || '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      validateOnBlur={false}
      validateOnChange
    >
      {({ values, isSubmitting, dirty, setFieldValue }) => {
        // Keep dirtyRef in sync with Formik's dirty state
        dirtyRef.current = dirty;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          handleSaveState(values as { rules: string });
        }, [values]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (reloadRules) void setFieldValue('rules', reloadRules);
        }, [setFieldValue]);
        return (
          <Stack sx={{ flex: 1, height: '100%' }}>
            <FormTextInput
              name="rules"
              multiline
              containerProps={{ sx: { flex: 1, height: '100%' } }}
              inputContainerProps={{ sx: { flex: 1, height: '100%' } }}
              sx={{
                height: '100%',
                flex: 1,
                '& textarea': { height: '100% !important', flex: 1, overflow: 'auto !important' },
              }}
            />
            <StandardFormButtons
              onClose={onClose}
              submitLabel="Save"
              cancelLabel={dirty && !isSubmitting ? 'Close without saving' : 'Close'}
            />
          </Stack>
        );
      }}
    </Form>
  );
};

export const useEditRulesModal = ({
  componentProps,
  modalProps,
}: {
  componentProps: Omit<EditRulesModalProps, 'onClose' | 'modalKey'> & { onClose?: VoidFunction };
  modalProps?: Partial<ModalProps>;
}) => {
  const { showModal: showEditOrganizationRulesModal } = useModal({
    component: EditRulesModal,
    componentProps,
    modalProps: {
      sx: {
        width: '80vw',
        maxWidth: '1200px',
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
        '& form': { flex: 1, height: '100%', '& .MuiFormGroup-root': { flex: 1, height: '100%' } },
      },
      ...modalProps,
    },
  });

  useEffect(() => {
    const reloadRules = localStorage.getItem(componentProps?.reloadKey || 'edit-rules');
    if (reloadRules) {
      showEditOrganizationRulesModal({ componentProps: { reloadRules } });
    }
  }, [componentProps?.reloadKey, showEditOrganizationRulesModal]);

  const showEditRulesModal = (dynamicComponentProps: Partial<EditRulesModalProps>) => {
    showEditOrganizationRulesModal({ componentProps: dynamicComponentProps });
  };

  return { showEditRulesModal };
};
